import { AfterViewInit, Component, Inject, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import * as GeoJSONModule from 'ol/format/GeoJSON.js';
const GeoJSON = GeoJSONModule.default as typeof GeoJSONModule.default;
import { EventsKey } from 'ol/events.js';
import { OLMapService } from '../../services/ol-map/ol-map.service';
import { unByKey } from 'ol/Observable.js';
import { AtlasClickResultComponent } from '../../components/ol-map/components/atlas-click-result.component';
import { TreinMapService } from '../services/trein-map.service';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom.js';
import { HttpClient } from '@angular/common/http';
import { BootstrapMediaService } from 'src/app/services/bootstrap-media.service';
import { TranslationHelperService } from 'src/app/services/translations/translation-helper.service';
import { catchError, defer, finalize, forkJoin, map, of, switchMap, take, tap, timeout } from 'rxjs';
import { BBOX } from 'src/assets/constants/constants';
import { unsubscribe } from 'src/app/utils/helpers';

@Component({
    standalone: false,
    selector: 'app-atlas-trein-map',
    templateUrl: './atlas-trein-map.component.html',
    styleUrls: ['./atlas-trein-map.component.scss'],
})
export class AtlasTreinMapComponent extends AtlasClickResultComponent implements OnInit, AfterViewInit, OnDestroy {
    private moveendKey!: EventsKey;
    private latestClickRequestId = 0;
    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router,
        public olMapService: OLMapService,
        public treinService: TreinMapService,
        protected http: HttpClient,
        protected breakpointObserver: BootstrapMediaService,
        protected translationHelper: TranslationHelperService,
        private readonly zone: NgZone,
        @Inject(PLATFORM_ID) private readonly platformId: object
    ) {
        super(http, breakpointObserver, translationHelper, olMapService);
    }
    ngOnInit(): void {}

    ngAfterViewInit(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        this.olMapService.mapReady$.pipe(take(1)).subscribe(() => {
            this.initializeMap();
        });
    }

    private initializeMap(): void {
        this.fitInitialMapView();
        this.treinService.getFilter('trein-filter');
        this.treinService.softenBasemap();
        this.loadLayer();
        this.updateBbox();
        this.moveendKey = this.olMapService.map.on('moveend', () => {
            this.updateBbox();
        });
        this.mapOperations();
    }

    fitInitialMapView() {
        const bBoxArr = this.olMapService.mapConfig?.[BBOX] ? this.olMapService.mapConfig[BBOX].split(',').map((bbox: string) => Number(bbox)) : '';
        if (bBoxArr != '') {
            this.olMapService.map.getView().fit(bBoxArr);
        }
        const queryParams = {
            bbox: bBoxArr.toString(),
        };
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams,
            replaceUrl: true,
            queryParamsHandling: 'merge',
        });
    }

    updateBbox() {
        const extent = this.olMapService.map.getView().calculateExtent(this.olMapService.map.getSize()).toString();
        const queryParams = {
            bbox: extent,
        };
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams,
            replaceUrl: true,
            queryParamsHandling: 'merge',
        });
    }

    mapOperations(): void {
        this.subscriptions.push(
            this.olMapService.mapClick$
                .pipe(
                    map((event) => ({ event, requestId: ++this.latestClickRequestId })),
                    tap(() =>
                        this.zone.run(() => {
                            this.treinService.removeVectorLayers();
                            this.olMapService.details = [];
                            this.olMapService.detailsLoading = true;
                            this.olMapService.notifyDetailStateChanged();
                        })
                    ),
                    switchMap(({ event, requestId }) =>
                        defer(() => {
                            const requests = this.treinService.mapOperations(event);
                            if (!requests?.length) {
                                return of({ requestId, featureCollections: [] });
                            }

                            return forkJoin(requests).pipe(map((featureCollections) => ({ requestId, featureCollections })));
                        }).pipe(
                            timeout(15000),
                            catchError(() => of({ requestId, featureCollections: [] })),
                            finalize(() =>
                                this.zone.run(() => {
                                    if (requestId === this.latestClickRequestId) {
                                        this.olMapService.detailsLoading = false;
                                        this.olMapService.notifyDetailStateChanged();
                                    }
                                })
                            )
                        )
                    )
                )
                .subscribe((result) => {
                    if (result.requestId !== this.latestClickRequestId) {
                        return;
                    }

                    this.zone.run(() => {
                        this.renderMultipleResponses(
                            result.featureCollections,
                            () => this.treinService.removeVectorLayers(),
                            (features) => {
                                const format = new GeoJSON();
                                this.updateDetails(features[0]);
                                this.addVectorLayer(features[0], format);
                            }
                        );
                        this.olMapService.notifyDetailStateChanged();
                    });
                })
        );
    }
    addVectorLayer(feature: Feature<Geometry>, format: any) {
        this.treinService.addVectorLayer(feature, format);
    }
    setFilter(): void {}
    getLayerDetails(feature: Feature<Geometry>): void {}
    getLayerName(): string {
        return '';
    }
    updateDetails(feature: Feature<Geometry>) {
        this.treinService.updateDetails(feature);
    }
    getStyleName(): string {
        return '';
    }
    loadLayer(): void {
        this.treinService.loadLayer();
    }
    ngOnDestroy(): void {
        if (this.moveendKey) unByKey(this.moveendKey);
        this.treinService.releaseLayer();
        unsubscribe(this.subscriptions);
    }
}
